import React, { useState, useEffect, act } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InputLabel, Box, Typography, LinearProgress, Paper, List, ListItem, ListItemText, IconButton, colors, Tooltip } from '@mui/material';
import {Icon} from 'semantic-ui-react';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import Grid from '@mui/material/Grid2';
import Confetti from 'react-confetti';
import { addAchievementAsync, updateAchievementAsync, selectAchievement, 
  clearSelectedAchievement, fetchAchievements, deleteAchievementAsync,
  fetchAchievementsListUsers, fetchAchievementsListTeams, 
  clearAchievements} from '../app/achievementsSlice';
import { post } from '@okta/okta-auth-js';
import { addActivityAsync, updateActivityAsync, selectActivity, clearSelectedActivity, 
  fetchActivities, fetchActivitiesListUsersFilterSrv, deleteActivityAsync } from '../app/activitiesSlice';
import Debug from '../utils/logger';
import { isActivityByHours } from './PanelActivities';
import {Card, CardMedia, CardContent} from '@msi/cobalt-react/card';
import ProgressBar from '@msi/cobalt-react/progress_bar';
import Button from '@msi/cobalt-react/button';
import ArrowLeft from '@msi/cobalt-react/icons/arrow-left';
import ArrowRight from '@msi/cobalt-react/icons/arrow-right';
import ArrowDown from '@msi/cobalt-react/icons/arrow-down';
import {Dropdown, DropdownItem} from '@msi/cobalt-react/dropdown';
import { ToastContainer, addToast } from '@msi/cobalt-react/toast';
import { useTheme } from '@mui/material/styles';
import download from 'downloadjs';
import { saveAs } from 'file-saver';

const debug = new Debug('PanelDashboard');
debug.enabled = true;
debug.enableActions({'useEffect': true});


const PanelDashboard = () => {
  const users = useSelector((state) => state.users.users);
  const achievements = useSelector((state) => state.achievements.achievements);
  const scoring = useSelector((state) => state.scoring.scoring) || [];
  const badges = useSelector((state) => state.scoring.badges) || [];
  const activities = useSelector((state) => state.activities.activities);
  const user = useSelector((state) => state.login);
  const dispatch = useDispatch();
  const [showConfetti, setShowConfetti] = useState(false);
  const achievementsStatus = useSelector((state) => state.achievements.status);
  const activitiesStatus = useSelector((state) => state.activities.status);
  const [achievementsProcessed, setAchievementsProcessed] = useState(null);
  const [activitiesProcessed, setActivitiesProcessed] = useState(false);
  const [leaderboardOptions, setLeaderboardOptions] = useState("All");
  const [leaderboard, setLeaderboard] = useState([]);
  const [paginatedLeaderboard, setPaginatedLeaderboard] = useState([]);
  const isDarkMode = useSelector((state) => state.theme.mode === 'dark');
  
  //Calculate the progress for each user based on their activities and scoring
  //Allows to know when the user has reached a new level compared to what the database has
  const calculateProgress = (userId) => {
    const userActivities = activities.filter(act => act.coreId === userId);
    let totalPoints = 0;
    userActivities.forEach((act) => {
      const stage = scoring.find(stage => stage.id === act.activityId);
      if (stage) {
        if (isActivityByHours(act.activityId)) {
          const hours = parseFloat(act.hours) || 0;
          const points = Math.floor(hours * stage.score);
          totalPoints += points;
        }
        else {
          totalPoints += stage.score;
        }
      }
    });
    return totalPoints;
  };

  //This function is used to determine if the user should be able to move on from the 4th level
  const hasFiftyAndHundredPointActivities = (userId) => {
    const userActivities = activities.filter(act => act.coreId === userId);
    let hasFifty = false;
    let hasHundred = false;
    userActivities.forEach((act) => {
      const stage = scoring.find(stage => stage.id === act.activityId);
      if (stage) {
        let points = 0;
        
        points = stage.score;

        if (points === 50) hasFifty = true;
        if (points >= 100) hasHundred = true;
      }
    });
    return hasFifty && hasHundred;
  };

  const updateLeaderboard = () => {
    const uniqueActivities = activities.filter((activity, index, self) => 
      index === self.findIndex((a) => a.coreId === activity.coreId)
    );
    const newLeaderboard = uniqueActivities.sort((a, b) => calculateProgress(b.coreId) - calculateProgress(a.coreId));
    setLeaderboard(newLeaderboard);
    setPaginatedLeaderboard(newLeaderboard.slice(page * usersPerPage, (page + 1) * usersPerPage));
  };

  const [page, setPage] = useState(0);
  const usersPerPage = 10;

  const handleNextPage = () => {
    if ((page + 1) * usersPerPage < leaderboard.length) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  //Initial fetch of achievements and activities
  useEffect(() => {
    if (!activitiesProcessed) {
      dispatch(clearAchievements());
      dispatch(fetchAchievementsListUsers([user.coreId]));
      //Fetch All Activities for proper leaderboard
      if (leaderboardOptions === "All") {
        debug.log("Fetching all activities");
        dispatch(fetchActivities());
        dispatch
      }
      else {
        debug.log("Fetching activities for users team");
        dispatch(fetchActivitiesListUsersFilterSrv([user.coreId]));
      }
      setActivitiesProcessed(true);
      setAchievementsProcessed(false); // Reset achievements processing to ensure we re-process after fetching activities
    }
  }, [activities, badges, user.coreId, dispatch, leaderboardOptions]);

  //Checks against the db to see if the user should get a new badge
  useEffect( () => {
    async function checkForNewBadge () {
      if (achievementsStatus === 'succeeded' && !achievementsProcessed && achievementsProcessed !== null) {
        setAchievementsProcessed(true);
        const userProgress = calculateProgress(user.coreId);
        const userBadges = badges.filter(badge => userProgress >= badge.score);
        const currentLevel = userBadges.length;
        
        const userAchievements = achievements.filter(ach => ach.coreId === user.coreId);

        //Prevent users from reaching the Expert or Guru level without completing 50 and 100 point activities
        if (userBadges.length >= 4) {
          if (!hasFiftyAndHundredPointActivities(user.coreId)) {
            if (userAchievements.length === 0 ||userAchievements[userAchievements.length - 1].badgeId !== 'PRO')
            {
              dispatch(addAchievementAsync({ coreId: user.coreId, badgeId: 'PRO', date: new Date()}));
            }
            addToast('error', 'You need to complete at least one activity with 50 and 100 points to reach this level.');
            return;
          }
        }

        //Add an Achievement object to the db if the user doesn't aleady have one
        if (userBadges.length > 0 && userAchievements.length == 0) {
          dispatch(addAchievementAsync({ coreId: user.coreId, badgeId: userBadges[userBadges.length - 1].id, date: new Date()}));
          dispatch(fetchAchievementsListUsers([user.coreId]));
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 10000); // Stop confetti after 5 seconds
        }
        //Create a new Achievement object if the user has reached a higher level and display confetti
        if (userAchievements.length > 0) {
          const achievement = userAchievements[userAchievements.length - 1];
          if (currentLevel > 0 && achievement.badgeId !== userBadges[userBadges.length - 1].id) {

            await dispatch(addAchievementAsync({ coreId: user.coreId, badgeId: userBadges[userBadges.length - 1].id, date: new Date()}));

            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 10000); // Stop confetti after 5 seconds
            addToast('success', `Congratulations! You have reached a new level: ${userBadges[userBadges.length - 1].badge}`);
          }
        }
      }
    }
    checkForNewBadge();
  }, [achievementsStatus, achievements]);

  useEffect(() => {
    if (activitiesStatus === 'succeeded' && activitiesProcessed) {
      updateLeaderboard();
    }
  }, [achievementsStatus, activitiesStatus, activities, achievements]);

  useEffect(() => {
    setPaginatedLeaderboard(leaderboard.slice(page * usersPerPage, (page + 1) * usersPerPage));
  }, [page, leaderboard]);

  return (
    <Box sx={{ backgroundColor: (theme) => theme.palette.secondary.main, color: (theme) => theme.palette.text.main, padding: 2 }}>
      <ToastContainer />
      {showConfetti && <Confetti />}
      <Typography variant="h4" >Dashboard</Typography>
      <Box display="flex" flexDirection="row" flexWrap="wrap" justifyContent="space-around" mt={2}>
        {badges.map((badge) => {
          const isBadgeComplete = calculateProgress(user.coreId) >= badge.score;
            const canDownload = isBadgeComplete && (!badge.extraRequirements || hasFiftyAndHundredPointActivities(user.coreId));
            return ( 
              <Tooltip title={canDownload ? "Click to download badge" : ""} placement="top" arrow>
              <div
                key={badge.id}
                style={{ margin: 20, cursor: canDownload ? "pointer" : "default", opacity: canDownload ? 1 : 0.7 }}
                onClick={() => {
                if (canDownload) saveAs(`/${badge.id}.png`, `${badge.badge}.png`);
                }}
              >
                <Card>
                <div style={{ paddingTop: 10 }}>
                  <CardMedia>
                  <img src={`/${badge.id}.png`} alt={badge.badge} />
                  </CardMedia>
                </div>
                <CardContent>
                  <Typography variant="h6">{badge.badge}</Typography>
                  {isBadgeComplete ? (
                  <ProgressBar progress={100} />
                  ) : (
                  <ProgressBar progress={(calculateProgress(user.coreId) / badge.score) * 100} />
                  )}
                  {isBadgeComplete ? (
                  <Typography variant="body2">Completed!</Typography>
                  ) : (
                  <Typography variant="body2">
                    {calculateProgress(user.coreId)} / {badge.score} points
                  </Typography>
                  )}
                  {isBadgeComplete && badge.extraRequirements && !hasFiftyAndHundredPointActivities(user.coreId) ? (
                  <Typography variant="body2" color="error">
                    You must complete at least one 50 point item and at least one 100 point item before you can achieve this badge.
                  </Typography>
                  ) : null}
                </CardContent>
                </Card>
              </div>
              </Tooltip>
            )})}
      </Box>
      <Box sx={{ my:2 }}>
        <Typography variant="h5">Leaderboard</Typography>
        <Grid container justifyContent="flex-end" paddingRight={3}>
          <Typography variant="body1" sx={{ marginRight: 2 }}>
            Page {page + 1} of {Math.ceil(leaderboard.length / usersPerPage)}
          </Typography>
          <Button buttonType='icon' icon={true} onClick={handlePreviousPage}>
            <ArrowLeft />
          </Button>
          <Button buttonType='icon' icon={true} onClick={handleNextPage}>
            <ArrowRight />
          </Button>
        </Grid>
        <InputLabel shrink sx={{color: (theme) => theme.palette.text.main, px: 0.5 }}>Select Leaderboard</InputLabel>
        <Dropdown
            anchor={function noRefCheck(){
              return <Button buttonType='secondary' size='large'>{leaderboardOptions}<ArrowDown /></Button>;
            }}
            position='bottom'
          >
            <DropdownItem
              onClick={() => {
                setLeaderboardOptions("All");
                setActivitiesProcessed(false);
              }}
            >All</DropdownItem>
            <DropdownItem
              onClick={() => {
                setLeaderboardOptions("Team");
                setActivitiesProcessed(false);
              }}
            >Team</DropdownItem>
          </Dropdown>
        <Paper elevation={3} sx={{ padding: 2, margin: 2, backgroundColor: (theme) => theme.palette.appLayer.main, color: (theme) => theme.palette.text.main }}>
          <List>
            {paginatedLeaderboard.map((user, index) => (
              <ListItem key={user.coreId}>
                <ListItemText 
                primary={`${page * usersPerPage + index + 1}. ${user.name === "" ? user.coreId : user.name}`}
                secondary={`${calculateProgress(user.coreId)} points`}
                slotProps={{
                  secondary: {
                      style: {
                        color: (theme) => theme.pallete.text.secondary
                      }
                  }
                }} />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>
      <Box mt={4}>
        <Typography variant="h5">How to Get More Points</Typography>
        <Paper elevation={3} sx={{ padding: 2, margin: 2, backgroundColor: (theme) => theme.palette.appLayer.main, color: (theme) => theme.palette.text.main }}>
          <List sx={{ listStyle: "decimal" }}>
            {scoring.filter(stage => stage.enabled).map((stage, index) => (
              <ListItem key={stage.id}>
                <ListItemText primary={index + 1 + ". " + stage.stage} secondary={`${stage.score} points`} />
              </ListItem>
            ))}
          </List>
          <Typography variant="body1">
            Participate in activities, complete achievements, and accumulate points to earn badges. Check the scoring rules for more details.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default PanelDashboard;
